/**
 * RequestsPane (INBOX-REQUESTS-01, Seth 2026-09-02: the Today tile "Time Off
 * Requests" "should just go to the row inside of inbox that will display the
 * time off requests" — not the weekly install schedule).
 *
 * The Inbox grows a fifth tab, Requests: things a teammate is waiting on a
 * yes/no for. First resident: time-off requests from the field app
 * (tech_availability rows with status = requested). The rail lists them
 * like conversations; the right pane shows the request with Approve / Deny
 * — the same mutation the Team page uses, so the tech still hears back in
 * chat the moment you decide (timeoff_decision trigger). Deep link:
 * /inbox?tab=requests&c=req:<id>. Later residents: time-fix requests from
 * the time engine, schedule-change asks.
 */
import { useState } from "react";
import { Palmtree, Check, X, CalendarDays, ArrowRight, Clock } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useNavigateWithBack } from "@/lib/navBack";
import { TechAvatar } from "@/components/TechAvatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { useAllActiveTechs } from "@/features/board/api";
import { AVAIL_KIND_LABEL, type AvailKind } from "@/features/board/availability";
import { useTimeOffRequests, useDecideTimeOff, type TimeOffRequest } from "@/features/team/team";

export const REQUEST_KEY_PREFIX = "req:";

/** Pending requests visible to this user (approvers only). */
export function useInboxRequests() {
  const canApprove = useCan("team.approve_timeoff");
  const q = useTimeOffRequests();
  return { canApprove, requests: canApprove ? (q.data ?? []) : [], isLoading: canApprove && q.isLoading };
}

function useTechName() {
  const { data: techs } = useAllActiveTechs();
  return (empno: string) => {
    const t = (techs ?? []).find((x) => x.empno === empno);
    return (t?.firstname && t?.lastname ? `${t.firstname} ${t.lastname}` : t?.firstname) ?? t?.empname ?? empno;
  };
}

export function spanOf(r: TimeOffRequest): string {
  const s = new Date(r.start_at);
  const e = new Date(new Date(r.end_at).getTime() - (r.all_day ? 60000 : 0));
  const f = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  if (r.all_day) return f(s) === f(e) ? f(s) : `${f(s)} – ${f(e)}`;
  const t = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${f(s)}, ${t(s)} – ${t(e)}`;
}

function daysOf(r: TimeOffRequest): number {
  const s = new Date(r.start_at).getTime();
  const e = new Date(r.end_at).getTime();
  return Math.max(1, Math.round((e - s) / 86400000));
}

function requestedAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

/** The rail rows for the Requests tab. */
export function RequestsList({ requests, selectedKey, onOpen }: {
  requests: TimeOffRequest[];
  selectedKey: string | null;
  onOpen: (key: string) => void;
}) {
  const nameOf = useTechName();
  if (requests.length === 0) {
    return (
      <EmptyState
        size="sm"
        className="py-14"
        icon={Palmtree}
        tone="emerald"
        title="No one is waiting on you"
        hint="Time off requests from the field app land here the moment a tech sends one."
      />
    );
  }
  return (
    <ul>
      {requests.map((r) => {
        const key = REQUEST_KEY_PREFIX + r.id;
        const active = key === selectedKey;
        const name = nameOf(r.empno);
        return (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => onOpen(key)}
              aria-current={active ? "true" : undefined}
              className={cn(
                "relative flex w-full items-start gap-3 border-b border-hairline px-3.5 py-3 text-left transition-colors",
                active
                  ? "bg-brand-50/70 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-r-full before:bg-brand-600"
                  : "hover:bg-slate-50",
              )}
            >
              <TechAvatar empno={r.empno} name={name} size="md" className="mt-0.5" />
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-1.5">
                  <span className="truncate text-[14px] font-extrabold text-slate-900">{name}</span>
                  <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">{requestedAgo(r.created_at)}</span>
                </span>
                <span className="mt-0.5 block truncate text-[12.5px] font-medium leading-snug text-slate-600">
                  {AVAIL_KIND_LABEL[r.kind as AvailKind] ?? r.kind} · {spanOf(r)}
                </span>
                <span className="mt-1.5 flex flex-wrap items-center gap-1">
                  <span className="rounded-full bg-amber-50 px-1.5 py-px text-[10.5px] font-bold text-amber-700 ring-1 ring-amber-200">
                    Time Off Request
                  </span>
                  {r.all_day && daysOf(r) > 1 && (
                    <span className="rounded-full bg-slate-50 px-1.5 py-px text-[10.5px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200/70">
                      {daysOf(r)} days
                    </span>
                  )}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** The right pane for one request: who, when, why, Approve / Deny. */
export function RequestDetail({ request, onDecided }: { request: TimeOffRequest; onDecided: () => void }) {
  const nameOf = useTechName();
  const decide = useDecideTimeOff();
  const navigate = useNavigateWithBack();
  const [denying, setDenying] = useState(false);
  const [note, setNote] = useState("");
  const name = nameOf(request.empno);
  const kind = AVAIL_KIND_LABEL[request.kind as AvailKind] ?? request.kind;
  const weekIso = new Date(request.start_at).toLocaleDateString("en-CA");

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-hairline bg-white px-5 py-3.5">
        <TechAvatar empno={request.empno} name={name} size="md" />
        <div className="min-w-0">
          <h2 className="truncate text-[15px] font-extrabold text-slate-900">{name}</h2>
          <p className="text-xs text-slate-500">Time off request · sent {requestedAgo(request.created_at)}</p>
        </div>
        <span className="ml-auto rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700 ring-1 ring-amber-200">
          Waiting on you
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="mx-auto max-w-xl rounded-2xl border border-amber-200/70 bg-amber-50/40 p-5">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-amber-600 ring-1 ring-amber-200">
              <Palmtree className="h-4.5 w-4.5" />
            </span>
            <div className="min-w-0">
              <p className="text-base font-bold text-slate-900">{kind}</p>
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-slate-700">
                <CalendarDays className="h-4 w-4 text-slate-400" /> {spanOf(request)}
                {request.all_day && daysOf(request) > 1 && <span className="text-slate-500">· {daysOf(request)} days</span>}
              </p>
              {request.title && (
                <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm text-slate-700 ring-1 ring-amber-100">&ldquo;{request.title}&rdquo;</p>
              )}
              <p className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500">
                <Clock className="h-3.5 w-3.5" /> Requested {new Date(request.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ id: request.id, decision: "approved" }, { onSuccess: onDecided })}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> Approve
            </button>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => { setDenying((v) => !v); setNote(""); }}
              className="inline-flex items-center gap-1.5 rounded-md bg-white px-3.5 py-2 text-sm font-semibold text-rose-600 ring-1 ring-rose-200 hover:bg-rose-50"
            >
              <X className="h-4 w-4" /> Deny…
            </button>
            <button
              type="button"
              onClick={() => navigate(`/team?pane=week&wk=${weekIso}`)}
              className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"
            >
              See that week&apos;s install schedule <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {denying && (
            <div className="mt-3 flex items-center gap-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoFocus
                placeholder="Why? (goes to the tech — optional)"
                className="flex-1 rounded-md border border-hairline bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              />
              <button
                type="button"
                disabled={decide.isPending}
                onClick={() => decide.mutate({ id: request.id, decision: "denied", note }, { onSuccess: onDecided })}
                className="rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                Confirm Deny
              </button>
            </div>
          )}
          {decide.isError && (
            <p className="mt-2 text-xs text-red-700">{(decide.error as Error)?.message ?? "Could not save the decision."}</p>
          )}
          <p className="mt-3 text-[11px] text-slate-500">The tech hears back in chat the moment you decide. Approved time off greys their lane on the board and the install week.</p>
        </div>
      </div>
    </div>
  );
}
